/**
 * 依赖注入的实现
 * 框架 https://inversify.io/
 *
 * 将通用功能模块封装成服务，服务通过依赖注入的方式引用其他服务。
 * 服务可以由common层实现，也可以由上层实现，依赖注入的方式可以抹平调用差异。
 * 依赖注入可以解耦服务的引用关系，便于单元测试。
 *
 * 使用方式：
 * （1）ServiceType 类型中增加服务的类型声明
 * （2）编写服务类。必须使用`@injectable`装饰
 * （3）注册服务。调用`bindService`注册服务
 * （4）调用服务。在服务类中调用另一个服务，使用`@inject`进行依赖注入；直接调用服务，使用`getService`方法
 */


import 'reflect-metadata';
import { Container, inject as injectOrigin } from 'inversify';
import { IBuildConfigService } from '../../typings/service/build-config';
import { IUserService } from '../../typings/service/user-service';
import { UploaderService } from './upload';

export { injectable } from 'inversify';

export type ServiceType = {
  'BuildConfigService': IBuildConfigService;
  'UserService': IUserService;
  'UploaderService': UploaderService;
};

export type ServiceName = Extract<keyof ServiceType, string>;

export const container = new Container();

/**
 * 注入服务
 * @param name 服务名
 * @param value 服务类
 */
export function bindService<K extends Extract<keyof ServiceType, string>>(
  name: K,
  value: new (...args: never[]) => ServiceType[K],
): void {
  container.bind(name).to(value);
}

/**
 * 获取服务
 * @param name 服务名
 * @returns 返回实例
 */
export function getService<K extends Extract<keyof ServiceType, string>>(name: K): ServiceType[K] {
  return container.get(name);
}

/**
 * 注入
 * @param name 服务名
 */
export function inject(name: ServiceName): ReturnType<typeof injectOrigin> {
  return injectOrigin(name);
}


/**
 * Common层服务请在这里注册
 */
bindService('UploaderService', UploaderService);
